import { useCanvasFileRecords } from "@/api/fileRecords";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  isCanvasIntrinsicRecord,
  ObjectActionMenu,
  ObjectActionMenuCloseProvider,
  useObjectActions,
  type ObjectActionHandlers,
} from "@/object-actions";
import { useAttachBulkAddToWorkspaceSubmenu } from "@/object-actions/transfer/useAddToWorkspaceSubmenu";
import { useCanvasRoute } from "@/pages/canvas/context/CanvasRouteContext";
import { useCanvasFileRecordMenuHandlerDeps } from "@/pages/canvas/hooks/useCanvasFileRecordMenuHandlerDeps";
import {
  collectFileEntityIds,
  deleteFileRowsForRemovedNodes,
} from "@/pages/canvas/utils/boardFileDelete";
import type { FileRecord } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import type { Node } from "@xyflow/react";
import { useReactFlow } from "@xyflow/react";
import {
  AlignCenterHorizontal,
  AlignCenterVertical,
  AlignEndHorizontal,
  AlignEndVertical,
  AlignStartHorizontal,
  AlignStartVertical,
  LayoutTemplate,
  MoreHorizontal,
  SquareMousePointer,
  Wand2,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  CanvasFloatingToolbarFrame,
  CanvasToolbarButton,
  CanvasToolbarDivider,
  CanvasToolbarDropdown,
  CanvasToolbarGroup,
  CanvasToolbarSubmenu,
} from "../components/toolbar";
import { useCanvas } from "../context/CanvasContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import {
  isZOrderEligibleForStacking,
  useCanvasZOrderActions,
} from "../hooks/useCanvasZOrderActions";
import { useCopyPaste } from "../hooks/useCopyPaste";
import { runAdoptionForNewSection } from "../hooks/useSectionPlacement";
import {
  computeMultiSelectLayout,
  computeSelectionBounds,
  computeWrapInSection,
  type MultiSelectLayoutMode,
} from "../utils/multiSelectLayout";
import { isLockableNode, isNodeLocked } from "../utils/nodeLock";
import { DEFAULT_SECTION_COLOR } from "./shared/CanvasColorPalettes";
import { CanvasFloatingToolbarPortal } from "./shared/CanvasFloatingToolbarPortal";
import { CanvasUnlockToolbar } from "./shared/CanvasUnlockToolbar";

const LAYOUT_ITEMS: {
  key: MultiSelectLayoutMode;
  label: string;
  icon: typeof Wand2;
}[] = [
  { key: "align-left", label: "Align left", icon: AlignStartVertical },
  {
    key: "align-h-center",
    label: "Align horizontal centers",
    icon: AlignCenterVertical,
  },
  { key: "align-right", label: "Align right", icon: AlignEndVertical },
  { key: "align-top", label: "Align top", icon: AlignStartHorizontal },
  {
    key: "align-v-center",
    label: "Align vertical centers",
    icon: AlignCenterHorizontal,
  },
  { key: "align-bottom", label: "Align bottom", icon: AlignEndHorizontal },
  { key: "tile", label: "Tile Layout", icon: LayoutTemplate },
];

interface MultiSelectFloatingToolbarProps {
  selectedNodes: Node[];
}

export function MultiSelectFloatingToolbar({
  selectedNodes,
}: MultiSelectFloatingToolbarProps) {
  const { getInternalNode, getNodes, updateNode } = useReactFlow();
  const { canvasId: canvasIdForDelete } = useCanvasRoute();
  const queryClientForDelete = useQueryClient();
  const {
    updateNodePositions,
    addNode,
    batchUpdateNodeParents,
    removeNodes,
    setNodeHidden,
    setNodesLocked,
  } = useCanvas();
  const { setToolbarPopoverOpen } = useCanvasUi();
  const { copy, duplicate } = useCopyPaste();
  const { handleNodeBringToFront, handleNodeSendToBack } =
    useCanvasZOrderActions();
  const [layoutOpen, setLayoutOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  // Bulk changes pass over locked nodes; copy and duplicate still take them.
  const unlockedNodes = useMemo(
    () => selectedNodes.filter((n) => !isNodeLocked(n)),
    [selectedNodes],
  );
  const allLocked = unlockedNodes.length === 0;

  // The container-class plain files behind the selection — the group Add to
  // workspace applies to these and only these. References (kind link), workspace
  // rows, blocks (canvas-intrinsic, D4), and non-file nodes (widgets,
  // connector entities) are skipped, so a mixed selection degrades instead
  // of breaking.
  const { data: canvasFileRows = [] } = useCanvasFileRecords(
    canvasIdForDelete ?? "",
  );
  const promotableRecords = useMemo(() => {
    const byId = new Map(canvasFileRows.map((r) => [r.id, r]));
    const out: FileRecord[] = [];
    for (const node of unlockedNodes) {
      const [entityId] = collectFileEntityIds([node]);
      const record = entityId ? byId.get(entityId) : undefined;
      if (
        record &&
        record.class === "canvas" &&
        record.kind !== "link" &&
        !record.path.endsWith("/") &&
        record.status !== "deleted" &&
        !isCanvasIntrinsicRecord({ record, nodeData: node.data })
      ) {
        out.push(record);
      }
    }
    return out;
  }, [canvasFileRows, unlockedNodes]);

  const bounds = useMemo(
    () => computeSelectionBounds(selectedNodes, getInternalNode),
    [selectedNodes, getInternalNode],
  );

  const handlePopoverOpenChange = useCallback(
    (open: boolean) => {
      setLayoutOpen(open);
      setToolbarPopoverOpen(open);
    },
    [setToolbarPopoverOpen],
  );

  const applyLayout = useCallback(
    (mode: MultiSelectLayoutMode) => {
      const nodes = getNodes().filter((n) =>
        unlockedNodes.some((selected) => selected.id === n.id),
      );
      const updates = computeMultiSelectLayout(nodes, mode, getInternalNode);
      if (updates.length === 0) return;

      updateNodePositions(
        updates.map(({ nodeId, position }) => ({ id: nodeId, position })),
      );
      for (const { nodeId, position } of updates) {
        updateNode(nodeId, { position });
      }
      handlePopoverOpenChange(false);
    },
    [
      unlockedNodes,
      getNodes,
      getInternalNode,
      updateNode,
      updateNodePositions,
      handlePopoverOpenChange,
    ],
  );

  const handleWrapInSection = useCallback(() => {
    const layout = computeWrapInSection(unlockedNodes, getInternalNode);
    if (!layout) return;

    const sectionId = uuidv4();
    const sectionNode: Node = {
      id: sectionId,
      type: "section",
      position: layout.sectionPosition,
      width: layout.sectionWidth,
      height: layout.sectionHeight,
      zIndex: -1,
      data: {
        title: "Section",
        sectionColor: DEFAULT_SECTION_COLOR,
        noFill: true,
        borderStyle: "solid",
        borderWidth: 2,
      },
    };

    addNode({ ...sectionNode, selected: false });

    batchUpdateNodeParents(
      layout.parentUpdates.map((u) => ({
        ...u,
        parentId: sectionId,
      })),
    );

    runAdoptionForNewSection({
      sectionNode,
      ghost: {
        position: layout.sectionPosition,
        width: layout.sectionWidth,
        height: layout.sectionHeight,
      },
      getNodes,
      getInternalNode,
      batchUpdateNodeParents,
    });
  }, [
    unlockedNodes,
    getInternalNode,
    getNodes,
    addNode,
    batchUpdateNodeParents,
  ]);

  const handleDelete = useCallback(() => {
    removeNodes(unlockedNodes.map((n) => n.id));
    // D2: a group delete resolves every file-backed node's row too — link
    // rows remove the reference, canvas files archive. Without this, the
    // rows survive as "not placed" drawer orphans.
    const entityIds = collectFileEntityIds(unlockedNodes);
    if (canvasIdForDelete && entityIds.length > 0) {
      void deleteFileRowsForRemovedNodes({
        queryClient: queryClientForDelete,
        canvasId: canvasIdForDelete,
        entityIds,
      });
    }
  }, [removeNodes, unlockedNodes, canvasIdForDelete, queryClientForDelete]);

  // Hide every selected node except sections — sections can't be hidden
  // singly either (their children would stay visible and strand), so the
  // group action matches the single-node menus.
  const handleHide = useCallback(() => {
    for (const node of unlockedNodes) {
      if (node.type === "section") continue;
      setNodeHidden(node.id, true);
    }
  }, [unlockedNodes, setNodeHidden]);

  const lockableIds = useMemo(
    () => unlockedNodes.filter(isLockableNode).map((n) => n.id),
    [unlockedNodes],
  );

  const moreMenuHandlers = useMemo<ObjectActionHandlers>(() => {
    const zOrderAnchorId = () =>
      unlockedNodes.find(isZOrderEligibleForStacking)?.id;
    return {
      copy,
      duplicate,
      "wrap-in-section": handleWrapInSection,
      "bring-to-front": () => {
        const anchor = zOrderAnchorId();
        if (anchor) handleNodeBringToFront(anchor);
      },
      "send-to-back": () => {
        const anchor = zOrderAnchorId();
        if (anchor) handleNodeSendToBack(anchor);
      },
      ...(lockableIds.length > 0
        ? { lock: () => setNodesLocked(lockableIds, true) }
        : {}),
      "remove-from-canvas": handleHide,
      // Submenu-only: the destination cascade is the interaction; the handler
      // exists so the resolver doesn't drop the action.
      "add-to-workspace": () => {},
      "bulk-delete": handleDelete,
    };
  }, [
    copy,
    duplicate,
    handleWrapInSection,
    unlockedNodes,
    handleNodeBringToFront,
    handleNodeSendToBack,
    handleHide,
    handleDelete,
    lockableIds,
    setNodesLocked,
  ]);

  const { actions: moreMenuActions } = useObjectActions({
    kind: { type: "multi-selection", kinds: [] },
    surface: "context-menu",
    enabled: selectedNodes.length > 1,
    // duplicate is gated on canDuplicateSelection for the workspace list; on the
    // canvas any multi-selection is duplicable, so opt it in explicitly.
    state: {
      canDuplicateSelection: true,
      hasPromotableFiles: promotableRecords.length > 0,
    },
    handlers: moreMenuHandlers,
  });
  // Each block's live node box rides its promote, so the records remember
  // the size the user last saw them at.
  const bulkHandlerDeps = useCanvasFileRecordMenuHandlerDeps(
    canvasIdForDelete,
    null,
  );
  const moreActions = useAttachBulkAddToWorkspaceSubmenu(
    moreMenuActions,
    promotableRecords,
    bulkHandlerDeps?.resolveBlockDisplaySize,
  );

  if (!bounds || selectedNodes.length < 2) return null;

  if (allLocked) {
    return (
      <CanvasUnlockToolbar
        nodeIds={selectedNodes.map((n) => n.id)}
        positionAbsoluteX={bounds.centerX - bounds.width / 2}
        positionAbsoluteY={bounds.topY}
        width={bounds.width}
        topOffset={64}
      />
    );
  }

  return (
    <CanvasFloatingToolbarPortal
      positionAbsoluteX={bounds.centerX - bounds.width / 2}
      positionAbsoluteY={bounds.topY}
      width={bounds.width}
      topOffset={64}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      <TooltipProvider delayDuration={300}>
        <CanvasFloatingToolbarFrame onMouseDown={(e) => e.preventDefault()}>
          <CanvasToolbarSubmenu
            open={layoutOpen}
            onOpenChange={handlePopoverOpenChange}
            side="top"
            trigger={
              <CanvasToolbarButton
                label="Layout"
                dropdown
                active={layoutOpen}
                aria-expanded={layoutOpen}
              >
                <LayoutTemplate className="size-4" />
              </CanvasToolbarButton>
            }
          >
            <CanvasToolbarGroup>
              {LAYOUT_ITEMS.map(({ key, label, icon: Icon }) => (
                <CanvasToolbarButton
                  key={key}
                  label={label}
                  onClick={() => applyLayout(key)}
                >
                  <Icon className="size-4" />
                </CanvasToolbarButton>
              ))}
            </CanvasToolbarGroup>
          </CanvasToolbarSubmenu>

          <CanvasToolbarButton
            label="Tidy up"
            onClick={() => applyLayout("tidy")}
          >
            <Wand2 className="size-4" />
          </CanvasToolbarButton>

          <CanvasToolbarButton
            label="Add to Section"
            onClick={handleWrapInSection}
          >
            <SquareMousePointer className="size-4" />
          </CanvasToolbarButton>

          <CanvasToolbarDivider />

          <CanvasToolbarDropdown
            label="More"
            showCaret={false}
            align="end"
            open={moreMenuOpen}
            onOpenChange={(open) => {
              setMoreMenuOpen(open);
              setToolbarPopoverOpen(open);
            }}
            icon={<MoreHorizontal className="size-4" />}
          >
            {/* The cascade commits from SubTrigger clicks, which Radix treats
                as "open" rather than "select" — the provider lets a commit
                dismiss the menu. */}
            <ObjectActionMenuCloseProvider
              value={() => {
                setMoreMenuOpen(false);
                setToolbarPopoverOpen(false);
              }}
            >
              <ObjectActionMenu
                actions={moreActions}
                kind={{ type: "multi-selection", kinds: [] }}
              />
            </ObjectActionMenuCloseProvider>
          </CanvasToolbarDropdown>
        </CanvasFloatingToolbarFrame>
      </TooltipProvider>
    </CanvasFloatingToolbarPortal>
  );
}
